import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { customFetcher } from '../useFetch';
import { listParameter, toQueryString } from './arnoAdminFilters';
import { IFlowView } from '../../Admin/ArnoTop/flowPresentation';
import { IEditorSection } from '../../Admin/ArnoTop/editorModel';

/**
 * Verwaltung der aus ArnoTop übernommenen Dokumentvorlagen (#1245).
 *
 * Alle Endpunkte liegen unter `/arno` und arbeiten auf dem kanonischen Modell in
 * der Datenbank — ArnoTop selbst ist nach dem Import keine Laufzeitquelle mehr.
 *
 * Grundsatz der ganzen Oberfläche: **eine menschliche Entscheidung gewinnt.**
 * Bestätigen, Ändern und Ignorieren schreiben `isHuman`, und kein Import
 * überschreibt das je wieder. Zurücksetzen gibt den automatischen Vorschlag
 * wieder frei.
 */

export type ArnoMappingStatus =
  | 'CONFIRMED'
  | 'MANUAL'
  | 'IGNORED'
  | 'SUGGESTED'
  | 'UNMAPPED';

export type ArnoSourceType =
  | 'DOCVARIABLE'
  | 'REGISTER'
  | 'FIELD'
  | 'TRANSACTION_TYPE'
  | 'BLOCK_REFERENCE';

export type ArnoReferenceState = 'open' | 'resolved' | 'ignored';

export interface IArnoPage<T> {
  rows: T[];
  total: number;
  page: number;
  pageSize: number;
}

export interface IArnoOverview {
  activeRun: {
    idArnoImportRun: number;
    status: string;
    sourceLabel: string | null;
    activatedAt: string | null;
    fileCount: number | null;
    parserVersion: string | null;
  } | null;
  lastRun: {
    idArnoImportRun: number;
    status: string;
    sourceLabel: string | null;
    startedAt: string | null;
    finishedAt: string | null;
    activatedAt: string | null;
    fileCount: number | null;
  } | null;
  templates: number;
  assignedTemplates: number;
  blocks: number;
  /** Betroffene Stellen im Bestand. */
  unresolvedReferences: number;
  /** Bäume mit mindestens einer Freigabe, Verbindungen insgesamt, betroffene Vorgangsarten. */
  assignments: number;
  assignedTypes: number;
  nativeTemplates: number;
  /** Verschiedene Legacy-Schluessel — so viele Entscheidungen stehen an. */
  unresolvedReferenceKeys: number;
  resolvedReferenceOverrides: number;
  humanMappings: number;
  mappings: Partial<Record<ArnoMappingStatus | 'total', number>>;
  cache?: unknown;
}

export type ArnoTemplateSource = 'ARNOTOP_IMPORTED' | 'DINO_NATIVE';
export type ArnoLifecycleStatus = 'DRAFT' | 'ACTIVE' | 'ARCHIVED';

/** Eine Freigabe: dieser Entscheidungsbaum gilt für diese DiNo-Rechtsgeschäftsart. */
export interface IArnoTemplateAssignment {
  idLegalTransactionType: number;
  legalTransactionType: string | null;
  /** Vorauswahl bei der Urkundenerzeugung; je Rechtsgeschäftsart höchstens eine. */
  isDefault: boolean;
  isEnabled: boolean;
  sortOrder: number;
  note: string | null;
  /** Die Rechtsgeschäftsart gibt es nicht mehr (Softdelete in DiNo). */
  missing: boolean;
}

/**
 * Ein Entscheidungsbaum: eine wiederverwendbare Logik zur Urkundenerzeugung.
 * Er ist **keine** Rechtsgeschäftsart, sondern wird beliebig vielen freigegeben.
 */
export interface IArnoDecisionTemplate {
  idDecisionTemplate: number;
  templateId: string;
  name: string;
  group: string;
  sourceType: ArnoTemplateSource;
  sourceSystem: string;
  lifecycleStatus: ArnoLifecycleStatus;
  isNative: boolean;
  versionId: number | null;
  versionNumber: number | null;
  latestVersionNumber: number | null;
  publishedVersionId: number | null;
  missingSince: string | null;
  variants: string[];
  handbookEntries: number;
  assignedTypes: number;
  enabledTypes: number;
  assignments: IArnoTemplateAssignment[];
  assignmentsMissing: number;
}

/** Was die Urkundenerzeugung für einen Vorgang zur Auswahl hat. */
export interface IArnoTemplatesForType {
  idLegalTransactionType: number;
  count: number;
  defaultTemplateId: number | null;
  templates: {
    idDecisionTemplate: number;
    templateId: string;
    name: string;
    group: string;
    sourceType: ArnoTemplateSource;
    lifecycleStatus: ArnoLifecycleStatus;
    isDefault: boolean;
    isEnabled: boolean;
    sortOrder: number;
  }[];
}

export interface IArnoMapping {
  idVariableMapping: number;
  sourceType: ArnoSourceType;
  sourceKey: string;
  scope: string;
  status: ArnoMappingStatus;
  targetSystem: string | null;
  targetPath: string | null;
  transform: string | null;
  confidence: number | null;
  suggestedBy: string | null;
  note: string | null;
  isHuman: boolean;
  context: {
    family?: string;
    occurrences?: number;
    blocks?: string[];
    legacyNames?: string[];
    owners?: string[];
    candidates?: string[];
  } | null;
}

export interface IArnoMappingDetail extends IArnoMapping {
  usage: {
    occurrences: number | null;
    blocks: string[];
    blockCount: number;
    legacyNames: string[];
    family: string | null;
    owners: string[];
    candidates: string[];
  };
}

export interface IArnoMappingFamily {
  family: string;
  total: number;
  open: number;
}

export interface IArnoSelectorField {
  path: string;
  /** Name des Feldes selbst — nie der Vorschauwert. */
  label: string;
  group: string;
  /** Kennung des fachlichen Bereichs (»LegalClientArray«, »urkunde« …). */
  groupId?: string;
  /** Ebenen innerhalb der Gruppe, z. B. »Käufer 2 › Anschrift«. */
  subgroup?: string | null;
  /** Name außerhalb der Auswahl, mit Ebene: »Käufer 2 · Nachname« (#1245, Teil 47). */
  displayName?: string;
  /** Kurze Erklärung, wo der Name allein nicht reicht. */
  description?: string | null;
  /** »normal«: fachlich; »expert«: technisch, doppelt, intern oder Altbestand. */
  visibility?: 'normal' | 'expert';
  /** Warum ein Feld nur für Experten ist. */
  reason?: string | null;
  /** »aggregate« fasst mehrere zusammen (Anzahl, Aufzählung), »single« ist ein Einzelwert. */
  kind?: 'aggregate' | 'single';
  role?: string | null;
  /** Fachliche Suchbegriffe (Name, Ebene, Rolle, Synonyme) — ohne Pfad. */
  search?: string[];
  /** Pfad, Feldname, ArnoTop-Namen — nur für die Suche in den technischen Feldern. */
  technicalSearch?: string[];
  arno: string[];
  valueType?: string;
  sample: string | null;
}

export interface IArnoSelectorGroup {
  id: string;
  label: string;
  fields: IArnoSelectorField[];
}

export interface IArnoSelectorCatalog {
  groups: IArnoSelectorGroup[];
  fields: number;
  dictionaryEntries: number;
  idLegalTransaction: number | null;
}

export interface IArnoUnresolvedReference {
  referenceKey: string;
  category: string | null;
  area: string | null;
  blockName: string | null;
  referenceKinds: string[];
  owners: string[];
  occurrences: number;
  candidates: string[];
  resolvedBlockKey: string | null;
  state: ArnoReferenceState;
  note?: string | null;
  mappingStatus?: ArnoMappingStatus;
}

export interface IArnoUnresolvedPage
  extends IArnoPage<IArnoUnresolvedReference> {
  states: Record<ArnoReferenceState, number>;
}

export interface IArnoBlockHit {
  blockKey: string;
  name: string;
  category: string;
  area: string;
}

export interface IArnoImportRun {
  idArnoImportRun: number;
  status: string;
  sourceLabel: string | null;
  sourceSystem: string | null;
  manifestHash: string | null;
  parserVersion: string | null;
  fileCount: number | null;
  startedAt: string | null;
  finishedAt: string | null;
  activatedAt: string | null;
  templates?: number;
  blocks?: number;
  message?: string | null;
}

/** Eine Zuordnung ist durch Typ, Schlüssel und Geltungsbereich bestimmt. */
export interface IArnoMappingRef {
  sourceType: ArnoSourceType;
  sourceKey: string;
  scope?: string;
}

const query = toQueryString;

/** Alles, was sich nach einer Entscheidung geändert haben kann. */
const ARNO_KEYS = [
  'arno/overview',
  'arno/templates',
  'arno/content',
  'arno/versions',
  'arno/flow',
  'arno/mappings',
  'arno/mapping',
  'arno/families',
  'arno/unresolved',
  'arno/field-usage',
  'arno/field-usage-detail',
  'arno/field-usage-ready',
  'arno/mapping-rules',
];

const invalidateArno = (queryClient: ReturnType<typeof useQueryClient>) =>
  Promise.all(
    ARNO_KEYS.map(key => queryClient.invalidateQueries({ queryKey: [key] }))
  );

//////////////////// Übersicht
export const useArnoOverview = () => {
  const { data, isLoading, isFetching, refetch } = useQuery<IArnoOverview>({
    refetchOnWindowFocus: false,
    queryKey: ['arno/overview'],
    queryFn: () => customFetcher({ endpoint: '/arno/admin/overview' }),
  });

  return {
    arnoOverview: data,
    isLoadingArnoOverview: isLoading,
    isFetchingArnoOverview: isFetching,
    refetchArnoOverview: refetch,
  };
};

//////////////////// Entscheidungsbäume und ihre Freigaben
export const useArnoDecisionTemplates = (filter?: {
  sourceType?: ArnoTemplateSource;
  lifecycleStatus?: ArnoLifecycleStatus;
  search?: string;
}) => {
  const parameters = query({
    sourceType: filter?.sourceType,
    lifecycleStatus: filter?.lifecycleStatus,
    search: filter?.search,
  });

  const { data, isLoading, isFetching, refetch } = useQuery<
    IArnoDecisionTemplate[]
  >({
    refetchOnWindowFocus: false,
    queryKey: ['arno/templates', parameters],
    queryFn: () =>
      customFetcher({ endpoint: `/arno/decision-templates?${parameters}` }),
  });

  return {
    decisionTemplates: data ?? [],
    isLoadingDecisionTemplates: isLoading,
    isFetchingDecisionTemplates: isFetching,
    refetchDecisionTemplates: refetch,
  };
};

/**
 * Freigaben eines Baums auf genau diese Menge bringen.
 *
 * Bewusst die ganze Menge statt einzelner Klicks: wer zwanzig Rechtsgeschäftsarten
 * auswählt, soll einmal speichern und nicht zwanzig Anfragen auslösen.
 */
export const useSetArnoTemplateAssignments = () => {
  const queryClient = useQueryClient();
  const { mutateAsync: setTemplateAssignments, isPending } = useMutation({
    mutationFn: ({
      idDecisionTemplate,
      assignments,
    }: {
      idDecisionTemplate: number;
      assignments: {
        idLegalTransactionType: number;
        isDefault?: boolean;
        isEnabled?: boolean;
        note?: string | null;
      }[];
    }) =>
      customFetcher({
        endpoint: `/arno/decision-templates/${idDecisionTemplate}/transaction-types`,
        method: 'PUT',
        body: { assignments },
      }),
    onSuccess: () => invalidateArno(queryClient),
  });

  return { setTemplateAssignments, isSettingTemplateAssignments: isPending };
};

export const useSetArnoTemplateDefault = () => {
  const queryClient = useQueryClient();
  const { mutateAsync: setTemplateDefault, isPending } = useMutation({
    mutationFn: ({
      idDecisionTemplate,
      idLegalTransactionType,
      isDefault,
    }: {
      idDecisionTemplate: number;
      idLegalTransactionType: number;
      isDefault: boolean;
    }) =>
      customFetcher({
        endpoint: `/arno/decision-templates/${idDecisionTemplate}/transaction-types/default`,
        method: 'POST',
        body: { idLegalTransactionType, isDefault },
      }),
    onSuccess: () => invalidateArno(queryClient),
  });

  return { setTemplateDefault, isSettingTemplateDefault: isPending };
};

export const useCreateArnoDecisionTemplate = () => {
  const queryClient = useQueryClient();
  const { mutateAsync: createDecisionTemplate, isPending } = useMutation({
    mutationFn: (body: { name: string; group?: string }) =>
      customFetcher({
        endpoint: '/arno/decision-templates',
        method: 'POST',
        body,
      }) as Promise<IArnoDecisionTemplate>,
    onSuccess: () => invalidateArno(queryClient),
  });

  return { createDecisionTemplate, isCreatingDecisionTemplate: isPending };
};

export const useSetArnoTemplateLifecycle = () => {
  const queryClient = useQueryClient();
  const { mutateAsync: setTemplateLifecycle, isPending } = useMutation({
    mutationFn: ({
      idDecisionTemplate,
      lifecycleStatus,
    }: {
      idDecisionTemplate: number;
      lifecycleStatus: ArnoLifecycleStatus;
    }) =>
      customFetcher({
        endpoint: `/arno/decision-templates/${idDecisionTemplate}/lifecycle`,
        method: 'POST',
        body: { lifecycleStatus },
      }),
    onSuccess: () => invalidateArno(queryClient),
  });

  return { setTemplateLifecycle, isSettingTemplateLifecycle: isPending };
};

/** Der Ablauf eines Entscheidungsbaums, nur lesend. */
export const useArnoTemplateFlow = (
  idDecisionTemplate?: number | null,
  variant = 'mGB'
) => {
  const { data, isLoading } = useQuery<IFlowView>({
    refetchOnWindowFocus: false,
    enabled: Boolean(idDecisionTemplate),
    queryKey: ['arno/flow', idDecisionTemplate, variant],
    queryFn: () =>
      customFetcher({
        endpoint: `/arno/decision-templates/${idDecisionTemplate}/flow?${query({ variant })}`,
      }),
  });

  return { templateFlow: data, isLoadingTemplateFlow: isLoading };
};

//////////////////// Nativer Editor
export interface IArnoTreeQuestion {
  questionId: string;
  prompt: string;
  answerType: string;
  options?: string[];
  blockKey?: string | null;
  /** In welchem Baustein sie gestellt wird — der Name, wenn sie selbst keinen Text hat. */
  blockName?: string;
}

export interface IArnoTemplateVersion {
  idDecisionTemplateVersion: number;
  idDecisionTemplate: number;
  templateId: string;
  name: string;
  group: string;
  description: string | null;
  versionNumber: number;
  /** STAGED = Entwurf, ACTIVE = veröffentlicht, SUPERSEDED = historisch. */
  status: string;
  origin: string;
  contentHash: string | null;
  isPublished: boolean;
  lifecycleStatus: ArnoLifecycleStatus;
  sourceType: ArnoTemplateSource;
  /** Geschrieben wird nur an einer nicht veröffentlichten Version. */
  editable: boolean;
  sections: IEditorSection[];
  /**
   * Alle Fragen dieser Version — auch die, die in einem eingefügten Unterbaustein gestellt
   * werden. Der Editor zeigt nur die Abschnitte des Rezepts; eine Bedingung darf sich aber auf
   * jede Frage des Laufs beziehen. Reine Auskunft, gespeichert wird sie nicht.
   */
  questions?: IArnoTreeQuestion[];
  /** Eingefügte Unterbausteine der Version — mögliche Ziele eines Bausteinverweises. */
  subBlocks?: { blockKey: string; name: string }[];
  changes?: {
    sections: number;
    blocks: number;
    blockVersionsCreated: number;
    blockVersionsReused: number;
  };
}

export interface IArnoVersionRow {
  idDecisionTemplateVersion: number;
  versionNumber: number;
  status: string;
  origin: string;
  createdAt: string | null;
  isPublished: boolean;
  fromImportRun: number | null;
}

export interface IArnoValidationIssue {
  severity: 'ERROR' | 'WARNING';
  code: string;
  message: string;
  details?: Record<string, unknown>;
  /** Zu welchem Abschnitt die Meldung gehört — der Editor springt dorthin. */
  sectionId?: string | null;
  sectionTitle?: string | null;
}

export interface IArnoValidation {
  idDecisionTemplateVersion: number;
  issues: IArnoValidationIssue[];
  errors: number;
  warnings: number;
  publishable: boolean;
}

/** Eine offene Stelle eines kopierten Baums — mit Sprungziel. */
export interface IArnoMigrationIssue {
  reasonCode: string;
  message: string;
  detail: Record<string, unknown>;
  sectionId: string | null;
  sectionTitle: string | null;
  blockKey: string;
  inParagraph?: boolean;
}

export interface IArnoMigration {
  idDecisionTemplateVersion: number;
  /** Offene Felder, die ArnoTop-Beteiligtenplätze sind — nicht zuordenbar (Teil 41). */
  participantSlots?: string[];
  blocks: number;
  nodes: number;
  open: number;
  groups: { reasonCode: string; count: number; examples: string[] }[];
  issues: IArnoMigrationIssue[];
}

/** Was an einem kopierten Baum noch zu entscheiden ist. */
export const useArnoTemplateMigration = (
  idDecisionTemplateVersion?: number | null,
  enabled = true
) => {
  const { data, isLoading, refetch } = useQuery<IArnoMigration>({
    refetchOnWindowFocus: false,
    enabled: Boolean(idDecisionTemplateVersion) && enabled,
    queryKey: ['arno/migration', idDecisionTemplateVersion],
    queryFn: () =>
      customFetcher({
        endpoint: `/arno/decision-template-versions/${idDecisionTemplateVersion}/migration`,
      }),
  });

  return {
    migration: data,
    isLoadingMigration: isLoading,
    refetchMigration: refetch,
  };
};

/**
 * Eine offene Legacy-Variable in DIESEM Entwurf durch ein DiNo-Feld ersetzen.
 *
 * Die globale ArnoTop-Zuordnung bleibt unberührt — sie zu ändern ist eine
 * Entscheidung mit ganz anderer Reichweite und gehört in die Zuordnungsverwaltung.
 */
export const useResolveArnoMigrationField = () => {
  const queryClient = useQueryClient();
  const { mutateAsync: resolveMigrationField, isPending } = useMutation({
    mutationFn: ({
      idDecisionTemplateVersion,
      variable,
      selector,
    }: {
      idDecisionTemplateVersion: number;
      variable: string;
      selector: string;
    }) =>
      customFetcher({
        endpoint: `/arno/decision-template-versions/${idDecisionTemplateVersion}/migration`,
        method: 'POST',
        body: { variable, selector },
      }),
    onSuccess: () => invalidateArno(queryClient),
  });

  return { resolveMigrationField, isResolvingMigrationField: isPending };
};

export const useArnoTemplateContent = (
  idDecisionTemplateVersion?: number | null
) => {
  const { data, isLoading, refetch } = useQuery<IArnoTemplateVersion>({
    refetchOnWindowFocus: false,
    enabled: Boolean(idDecisionTemplateVersion),
    queryKey: ['arno/content', idDecisionTemplateVersion],
    queryFn: () =>
      customFetcher({
        endpoint: `/arno/decision-template-versions/${idDecisionTemplateVersion}/content`,
      }),
  });

  return {
    templateContent: data,
    isLoadingTemplateContent: isLoading,
    refetchTemplateContent: refetch,
  };
};

export const useSaveArnoTemplateContent = () => {
  const queryClient = useQueryClient();
  const { mutateAsync: saveTemplateContent, isPending } = useMutation({
    mutationFn: ({
      idDecisionTemplateVersion,
      sections,
      contentHash,
    }: {
      idDecisionTemplateVersion: number;
      sections: IEditorSection[];
      contentHash?: string | null;
    }) =>
      customFetcher({
        endpoint: `/arno/decision-template-versions/${idDecisionTemplateVersion}/content`,
        method: 'PUT',
        body: { sections, contentHash },
      }) as Promise<IArnoTemplateVersion>,
    onSuccess: () => invalidateArno(queryClient),
  });

  return { saveTemplateContent, isSavingTemplateContent: isPending };
};

export const useArnoTemplateVersions = (idDecisionTemplate?: number | null) => {
  const { data, isLoading, refetch } = useQuery<IArnoVersionRow[]>({
    refetchOnWindowFocus: false,
    enabled: Boolean(idDecisionTemplate),
    queryKey: ['arno/versions', idDecisionTemplate],
    queryFn: () =>
      customFetcher({
        endpoint: `/arno/decision-templates/${idDecisionTemplate}/versions`,
      }),
  });

  return {
    templateVersions: data ?? [],
    isLoadingTemplateVersions: isLoading,
    refetchTemplateVersions: refetch,
  };
};

export const useCreateArnoDraftVersion = () => {
  const queryClient = useQueryClient();
  const { mutateAsync: createDraftVersion, isPending } = useMutation({
    mutationFn: (idDecisionTemplate: number) =>
      customFetcher({
        endpoint: `/arno/decision-templates/${idDecisionTemplate}/versions`,
        method: 'POST',
      }) as Promise<IArnoTemplateVersion>,
    onSuccess: () => invalidateArno(queryClient),
  });

  return { createDraftVersion, isCreatingDraftVersion: isPending };
};

export const useValidateArnoTemplateVersion = () => {
  const { mutateAsync: validateTemplateVersion, isPending } = useMutation({
    mutationFn: (idDecisionTemplateVersion: number) =>
      customFetcher({
        endpoint: `/arno/decision-template-versions/${idDecisionTemplateVersion}/validate`,
        method: 'POST',
      }) as Promise<IArnoValidation>,
  });

  return { validateTemplateVersion, isValidatingTemplateVersion: isPending };
};

export const usePublishArnoTemplateVersion = () => {
  const queryClient = useQueryClient();
  const { mutateAsync: publishTemplateVersion, isPending } = useMutation({
    mutationFn: (idDecisionTemplateVersion: number) =>
      customFetcher({
        endpoint: `/arno/decision-template-versions/${idDecisionTemplateVersion}/publish`,
        method: 'POST',
      }) as Promise<IArnoTemplateVersion>,
    onSuccess: () => invalidateArno(queryClient),
  });

  return { publishTemplateVersion, isPublishingTemplateVersion: isPending };
};

export const useTestArnoTemplateVersion = () => {
  const { mutateAsync: testTemplateVersion, isPending } = useMutation({
    mutationFn: ({
      idDecisionTemplateVersion,
      idLegalTransaction,
      answers,
    }: {
      idDecisionTemplateVersion: number;
      idLegalTransaction?: number | null;
      answers?: Record<string, unknown>;
    }) =>
      customFetcher({
        endpoint: `/arno/decision-template-versions/${idDecisionTemplateVersion}/test`,
        method: 'POST',
        body: { idLegalTransaction, answers: answers ?? {} },
      }),
  });

  return { testTemplateVersion, isTestingTemplateVersion: isPending };
};

export const useCloneArnoTemplateAsNative = () => {
  const queryClient = useQueryClient();
  const { mutateAsync: cloneTemplateAsNative, isPending } = useMutation({
    mutationFn: ({
      idDecisionTemplate,
      name,
      variant,
    }: {
      idDecisionTemplate: number;
      name?: string;
      variant?: string;
    }) =>
      customFetcher({
        endpoint: `/arno/decision-templates/${idDecisionTemplate}/clone-native`,
        method: 'POST',
        body: { name, variant },
      }) as Promise<
        IArnoDecisionTemplate & {
          idDecisionTemplateVersion: number;
          conversion: {
            sourceName: string;
            variant: string;
            sections: number;
            subBlocks: number;
            blocks: number;
            nodesDirect: number;
            nodesAdapted: number;
            nodesMigration: number;
            mappingsUsed: number;
            /** Zähler der Formatierungübernahme, z. B. `layout:lineSpacing`. */
            rich?: Record<string, number>;
          };
        }
      >,
    onSuccess: () => invalidateArno(queryClient),
  });

  return { cloneTemplateAsNative, isCloningTemplate: isPending };
};

export const useUpdateArnoDecisionTemplate = () => {
  const queryClient = useQueryClient();
  const { mutateAsync: updateDecisionTemplate, isPending } = useMutation({
    mutationFn: ({
      idDecisionTemplate,
      ...body
    }: {
      idDecisionTemplate: number;
      name?: string;
      description?: string;
      group?: string;
    }) =>
      customFetcher({
        endpoint: `/arno/decision-templates/${idDecisionTemplate}/update`,
        method: 'POST',
        body,
      }),
    onSuccess: () => invalidateArno(queryClient),
  });

  return { updateDecisionTemplate, isUpdatingDecisionTemplate: isPending };
};

/** Die Abfrage der Urkundenerzeugung: was steht für diese Rechtsgeschäftsart bereit? */
export const useArnoTemplatesForLegalTransactionType = (
  idLegalTransactionType?: number | null
) => {
  const { data, isLoading } = useQuery<IArnoTemplatesForType>({
    refetchOnWindowFocus: false,
    enabled: Boolean(idLegalTransactionType),
    queryKey: ['arno/templates-for-type', idLegalTransactionType],
    queryFn: () =>
      customFetcher({
        endpoint: `/arno/legal-transaction-types/${idLegalTransactionType}/decision-templates`,
      }),
  });

  return { templatesForType: data, isLoadingTemplatesForType: isLoading };
};

//////////////////// Zuordnungen
export interface IArnoMappingFilter {
  page: number;
  pageSize: number;
  search?: string;
  status?: ArnoMappingStatus[];
  sourceType?: ArnoSourceType[];
  family?: string;
  human?: boolean;
}

export const useArnoMappings = (filter: IArnoMappingFilter) => {
  const parameters = query({
    page: filter.page,
    pageSize: filter.pageSize,
    search: filter.search,
    status: listParameter(filter.status),
    sourceType: listParameter(filter.sourceType),
    family: filter.family,
    human: filter.human === undefined ? undefined : String(filter.human),
  });

  const { data, isFetching, refetch } = useQuery<IArnoPage<IArnoMapping>>({
    queryKey: ['arno/mappings', parameters],
    queryFn: () => customFetcher({ endpoint: `/arno/mappings?${parameters}` }),
    placeholderData: keepPreviousData,
  });

  return {
    mappingPage: data,
    isFetchingMappings: isFetching,
    refetchMappings: refetch,
  };
};

export const useArnoMappingFamilies = () => {
  const { data, isLoading } = useQuery<IArnoMappingFamily[]>({
    refetchOnWindowFocus: false,
    queryKey: ['arno/families'],
    queryFn: () => customFetcher({ endpoint: '/arno/mappings/families' }),
  });

  return { mappingFamilies: data ?? [], isLoadingMappingFamilies: isLoading };
};

/** Eine Zuordnung mit ihrer Verwendung — die Grundlage jeder Entscheidung. */
export const useArnoMappingDetail = (reference: IArnoMappingRef | null) => {
  const parameters = reference
    ? query({
        sourceType: reference.sourceType,
        sourceKey: reference.sourceKey,
        scope: reference.scope ?? 'tenant',
      })
    : '';

  const { data, isFetching } = useQuery<IArnoMappingDetail>({
    refetchOnWindowFocus: false,
    enabled: Boolean(reference),
    queryKey: ['arno/mapping', parameters],
    queryFn: () =>
      customFetcher({ endpoint: `/arno/mappings/detail?${parameters}` }),
  });

  return { mappingDetail: data, isFetchingMappingDetail: isFetching };
};

export const useSetArnoMappingDecision = () => {
  const queryClient = useQueryClient();
  const { mutateAsync: setMappingDecision, isPending } = useMutation({
    mutationFn: (
      body: IArnoMappingRef & {
        status: 'CONFIRMED' | 'MANUAL' | 'IGNORED';
        targetPath?: string | null;
        note?: string | null;
      }
    ) =>
      customFetcher({
        endpoint: '/arno/mappings/decision',
        method: 'POST',
        body,
      }),
    onSuccess: () => invalidateArno(queryClient),
  });

  return { setMappingDecision, isSettingMappingDecision: isPending };
};

export const useResetArnoMapping = () => {
  const queryClient = useQueryClient();
  const { mutateAsync: resetMapping, isPending } = useMutation({
    mutationFn: (body: IArnoMappingRef) =>
      customFetcher({
        endpoint: '/arno/mappings/reset',
        method: 'POST',
        body,
      }),
    onSuccess: () => invalidateArno(queryClient),
  });

  return { resetMapping, isResettingMapping: isPending };
};

//////////////////// DiNo-Zielfelder
export const useArnoSelectorCatalog = (
  idLegalTransaction?: number | null,
  enabled = true
) => {
  const parameters = query({
    idLegalTransaction: idLegalTransaction ?? undefined,
  });

  const { data, isLoading } = useQuery<IArnoSelectorCatalog>({
    refetchOnWindowFocus: false,
    enabled,
    queryKey: ['arno/selectors', parameters],
    queryFn: () => customFetcher({ endpoint: `/arno/selectors?${parameters}` }),
    // Der Katalog hängt am Datenmodell, nicht am Bestand — er darf stehen bleiben.
    staleTime: 10 * 60 * 1000,
  });

  return { selectorCatalog: data, isLoadingSelectorCatalog: isLoading };
};

//////////////////// Unaufgelöste Legacy-Verweise
export const useArnoUnresolvedReferences = (filter: {
  page: number;
  pageSize: number;
  search?: string;
  state?: ArnoReferenceState;
}) => {
  const parameters = query({
    page: filter.page,
    pageSize: filter.pageSize,
    search: filter.search,
    state: filter.state,
  });

  const { data, isFetching, refetch } = useQuery<IArnoUnresolvedPage>({
    queryKey: ['arno/unresolved', parameters],
    queryFn: () =>
      customFetcher({ endpoint: `/arno/import/unresolved?${parameters}` }),
    placeholderData: keepPreviousData,
  });

  return {
    unresolvedPage: data,
    isFetchingUnresolved: isFetching,
    refetchUnresolved: refetch,
  };
};

export const useArnoBlockSearch = (search: string) => {
  const { data, isFetching } = useQuery<IArnoBlockHit[]>({
    refetchOnWindowFocus: false,
    enabled: search.trim().length >= 2,
    queryKey: ['arno/blocks', search],
    queryFn: () =>
      customFetcher({
        endpoint: `/arno/blocks/search?${query({ search, limit: 25 })}`,
      }),
    placeholderData: keepPreviousData,
  });

  return { blockHits: data ?? [], isSearchingBlocks: isFetching };
};

export const useResolveArnoReference = () => {
  const queryClient = useQueryClient();
  const { mutateAsync: resolveReference, isPending } = useMutation({
    mutationFn: (body: {
      referenceKey: string;
      blockKey?: string | null;
      status: 'CONFIRMED' | 'IGNORED';
      note?: string | null;
    }) =>
      customFetcher({
        endpoint: '/arno/references/resolve',
        method: 'POST',
        body,
      }),
    onSuccess: () => invalidateArno(queryClient),
  });

  return { resolveReference, isResolvingReference: isPending };
};

export const useResetArnoReference = () => {
  const queryClient = useQueryClient();
  const { mutateAsync: resetReference, isPending } = useMutation({
    mutationFn: (body: { referenceKey: string }) =>
      customFetcher({
        endpoint: '/arno/references/reset',
        method: 'POST',
        body,
      }),
    onSuccess: () => invalidateArno(queryClient),
  });

  return { resetReference, isResettingReference: isPending };
};

//////////////////// Importläufe
export const useArnoImportRuns = () => {
  const { data, isLoading, refetch } = useQuery<IArnoImportRun[]>({
    refetchOnWindowFocus: false,
    queryKey: ['arno/runs'],
    queryFn: () => customFetcher({ endpoint: '/arno/import/runs?limit=50' }),
  });

  return {
    importRuns: data ?? [],
    isLoadingImportRuns: isLoading,
    refetchImportRuns: refetch,
  };
};

export const useActivateArnoImportRun = () => {
  const queryClient = useQueryClient();
  const { mutateAsync: activateImportRun, isPending } = useMutation({
    mutationFn: (idArnoImportRun: number) =>
      customFetcher({
        endpoint: `/arno/import/runs/${idArnoImportRun}/activate`,
        method: 'POST',
      }),
    onSuccess: async queryClientResult => {
      await queryClient.invalidateQueries({ queryKey: ['arno/runs'] });
      await invalidateArno(queryClient);
      return queryClientResult;
    },
  });

  return { activateImportRun, isActivatingImportRun: isPending };
};

//////////////////// Verwendungsnachweis der Legacy-Felder
export type ArnoEvidenceStatus =
  | 'PROVEN'
  | 'VERY_STRONG_EVIDENCE'
  | 'AMBIGUOUS'
  | 'UNKNOWN';

export type ArnoMappingKind = 'DIRECT' | 'COMPUTED' | 'NONE';

export type ArnoFieldState =
  | 'CONFIRMED'
  | 'READY_TO_CONFIRM'
  | 'NEEDS_EVIDENCE'
  | 'NO_DINO_SOURCE';

export interface IArnoFieldUsage {
  idLegacyFieldUsage: number;
  sourceKey: string;
  family: string | null;
  occurrences: number;
  plainUses: number;
  ifUses: number;
  /** Word-Bedingungen, die NUR an der fehlenden Zuordnung scheitern — die Hebelwirkung. */
  blockedIfs: number;
  safeIfs: number;
  /** Davon auf oberster Ebene des Bausteins. */
  safeTopLevelIfs: number;
  blockedTopLevelIfs: number;
  /**
   * Davon vom Klon wirklich erreichbar: jede äußere Bedingung darüber wird ebenfalls
   * übersetzt. Ohne diese Zahl liest sich die Hebelwirkung zu günstig.
   */
  safeReachableIfs: number;
  blockedReachableIfs: number;
  blockCount: number;
  templateCount: number;
  mappingStatus: ArnoMappingStatus;
  targetPath: string | null;
  evidenceStatus: ArnoEvidenceStatus;
  mappingKind: ArnoMappingKind;
  candidate: string | null;
  semantics: string | null;
  state: ArnoFieldState;
}

export interface IArnoFieldUsageDetail extends IArnoFieldUsage {
  detail: {
    ifClasses?: Record<string, number>;
    operators?: Record<string, number>;
    values?: [string, number][];
    trueTexts?: [string, number][];
    falseTexts?: [string, number][];
    contexts?: string[];
    categories?: [string, number][];
    areas?: [string, number][];
    spellings?: [string, number][];
    blocks?: string[];
    /** Welche äußere Bedingung hält die Stellen dieses Feldes auf — und wie oft. */
    ancestorBlockers?: [string, number][];
  };
  evidence: string[];
  reason: string | null;
  impact: { blockedIfs: number; blocks: number; templates: number };
}

export interface IArnoFieldUsageSummary {
  fields: number;
  blockingFields: number;
  blockedIfs: number;
  safeIfs: number;
  safeTopLevelIfs: number;
  blockedTopLevelIfs: number;
  safeReachableIfs: number;
  blockedReachableIfs: number;
  ifUses: number;
  analysedAt: string | null;
}

export interface IArnoFieldUsagePage extends IArnoPage<IArnoFieldUsage> {
  summary: IArnoFieldUsageSummary;
}

export interface IArnoParetoMark {
  fields: number;
  blockedIfs: number;
}

export interface IArnoPareto {
  fields: number;
  blockedIfs: number;
  marks: {
    p50?: IArnoParetoMark;
    p80?: IArnoParetoMark;
    p90?: IArnoParetoMark;
    p95?: IArnoParetoMark;
  };
}

export interface IArnoReadyBatch {
  rows: IArnoFieldUsage[];
  gainedIfs: number;
  blocks: number;
}

export interface IArnoSimulation {
  fields: {
    sourceKey: string;
    blockedIfs: number;
    blocks: number;
    templates: number;
    candidate: string | null;
    state: ArnoFieldState;
    evidenceStatus: ArnoEvidenceStatus;
  }[];
  gainedIfs: number;
  safeBefore: number;
  safeAfter: number;
  blockedBefore: number;
  blockedAfter: number;
}

export interface IArnoFieldUsageFilter {
  page: number;
  pageSize: number;
  search?: string;
  state?: ArnoFieldState;
  family?: string;
  onlyBlocking?: boolean;
}

/** Legacy-Felder nach Hebelwirkung — das Backend liest nur die Messtabelle. */
export const useArnoFieldUsage = (filter: IArnoFieldUsageFilter) => {
  const parameters = query({
    page: filter.page,
    pageSize: filter.pageSize,
    search: filter.search,
    state: filter.state,
    family: filter.family,
    onlyBlocking: filter.onlyBlocking ? 'true' : undefined,
  });

  const { data, isFetching } = useQuery<IArnoFieldUsagePage>({
    queryKey: ['arno/field-usage', parameters],
    queryFn: () =>
      customFetcher({ endpoint: `/arno/field-usage?${parameters}` }),
    placeholderData: keepPreviousData,
  });

  return { fieldUsagePage: data, isFetchingFieldUsage: isFetching };
};

export const useArnoFieldUsageDetail = (sourceKey: string | null) => {
  const { data, isFetching } = useQuery<IArnoFieldUsageDetail>({
    refetchOnWindowFocus: false,
    enabled: Boolean(sourceKey),
    queryKey: ['arno/field-usage-detail', sourceKey],
    queryFn: () =>
      customFetcher({
        endpoint: `/arno/field-usage/${encodeURIComponent(sourceKey ?? '')}`,
      }),
  });

  return { fieldUsageDetail: data, isFetchingFieldUsageDetail: isFetching };
};

export const useArnoFieldUsagePareto = () => {
  const { data } = useQuery<IArnoPareto>({
    refetchOnWindowFocus: false,
    queryKey: ['arno/field-usage', 'pareto'],
    queryFn: () => customFetcher({ endpoint: '/arno/field-usage/pareto' }),
  });

  return { pareto: data };
};

export const useArnoReadyMappingBatch = () => {
  const { data, isFetching } = useQuery<IArnoReadyBatch>({
    refetchOnWindowFocus: false,
    queryKey: ['arno/field-usage-ready'],
    queryFn: () => customFetcher({ endpoint: '/arno/field-usage/ready' }),
  });

  return { readyBatch: data, isFetchingReadyBatch: isFetching };
};

/** Wirkung eines Stapels rechnen — ändert nichts, dient nur der Entscheidung. */
export const useSimulateArnoFieldMappings = () => {
  const { mutateAsync: simulateFieldMappings, isPending } = useMutation({
    mutationFn: (sourceKeys: string[]) =>
      customFetcher({
        endpoint: '/arno/field-usage/simulate',
        method: 'POST',
        body: { sourceKeys },
      }) as Promise<IArnoSimulation>,
  });

  return { simulateFieldMappings, isSimulatingFieldMappings: isPending };
};

/** Die Messung neu rechnen (einmaliger Auftrag über alle Bausteine). */
export const useAnalyseArnoFieldUsage = () => {
  const queryClient = useQueryClient();
  const { mutateAsync: analyseFieldUsage, isPending } = useMutation({
    mutationFn: () =>
      customFetcher({ endpoint: '/arno/field-usage/analyse', method: 'POST' }),
    onSuccess: async result => {
      await invalidateArno(queryClient);
      return result;
    },
  });

  return { analyseFieldUsage, isAnalysingFieldUsage: isPending };
};

/** Vorschau und Anwendung von »Migration erneut prüfen« an einem Entwurf. */
export interface IArnoRecheckField {
  variable: string;
  selector: string;
  places: number;
}

export interface IArnoRecheck {
  idDecisionTemplateVersion: number;
  isPublished: boolean;
  resolvableFields: IArnoRecheckField[];
  conditionsNeedingReclone: IArnoRecheckField[];
  resolvablePlaces: number;
  conditionPlaces: number;
  applied: number;
  migration: IArnoMigration;
}

export const useRecheckArnoMigration = () => {
  const queryClient = useQueryClient();
  const { mutateAsync: recheckMigration, isPending } = useMutation({
    mutationFn: ({
      idDecisionTemplateVersion,
      apply,
    }: {
      idDecisionTemplateVersion: number;
      apply?: boolean;
    }) =>
      customFetcher({
        endpoint: `/arno/decision-templates/versions/${idDecisionTemplateVersion}/migration/recheck`,
        method: 'POST',
        body: { apply: Boolean(apply) },
      }) as Promise<IArnoRecheck>,
    onSuccess: async result => {
      await invalidateArno(queryClient);
      return result;
    },
  });

  return { recheckMigration, isRecheckingMigration: isPending };
};

//////////////////// Zuordnungsregeln nummerierter Familien
export interface IArnoRuleIndexEntry {
  legacyBase: number;
  dinoBase: number;
  ordering?: string | null;
}

export interface IArnoRuleExample {
  sourceKey: string;
  values: Record<string, number>;
  selector: string | null;
  blockedIfs: number;
  blockedReachableIfs: number;
}

export interface IArnoRuleConflict {
  sourceKey: string;
  kind: 'EXPLICIT_MAPPING' | 'OVERLAPPING_RULE';
  detail: string | null;
}

export interface IArnoRuleImpact {
  sourcePattern: string;
  targetPattern: string | null;
  fields: number;
  occurrences: number;
  blockedIfs: number;
  /** Davon heute im Klon erreichbar — der Rest liegt unter einer offenen äußeren Bedingung. */
  blockedReachableIfs: number;
  examples: IArnoRuleExample[];
  conflicts: IArnoRuleConflict[];
  conflictCount: number;
  unresolved: { sourceKey: string; reason: string }[];
  unresolvedCount: number;
}

export interface IArnoMappingRule {
  idLegacyMappingRule: number;
  sourcePattern: string;
  targetPattern: string | null;
  indexRule: Record<string, IArnoRuleIndexEntry> | null;
  scope: string | null;
  family: string | null;
  semantics: string | null;
  evidenceStatus: ArnoEvidenceStatus;
  status: 'UNMAPPED' | 'SUGGESTED' | 'CONFIRMED' | 'IGNORED';
  isHuman: boolean;
  /** Was eine Bestätigung heute verhindert — leer, wenn nichts. */
  blocker: string | null;
  note: string | null;
  impact?: IArnoRuleImpact;
  parameters?: string[];
}

export const useArnoMappingRules = () => {
  const { data, isFetching } = useQuery<{
    rows: IArnoMappingRule[];
    total: number;
    confirmed: number;
  }>({
    refetchOnWindowFocus: false,
    queryKey: ['arno/mapping-rules'],
    queryFn: () =>
      customFetcher({ endpoint: '/arno/mapping-rules?withImpact=true' }),
  });

  return { mappingRules: data, isFetchingMappingRules: isFetching };
};

export const useSetArnoMappingRuleDecision = () => {
  const queryClient = useQueryClient();
  const { mutateAsync: setMappingRuleDecision, isPending } = useMutation({
    mutationFn: (body: {
      idLegacyMappingRule: number;
      status: 'CONFIRMED' | 'IGNORED';
      note?: string | null;
    }) =>
      customFetcher({
        endpoint: '/arno/mapping-rules/decision',
        method: 'POST',
        body,
      }) as Promise<IArnoMappingRule>,
    onSuccess: async result => {
      await invalidateArno(queryClient);
      return result;
    },
  });

  return { setMappingRuleDecision, isSettingMappingRuleDecision: isPending };
};

export const useResetArnoMappingRule = () => {
  const queryClient = useQueryClient();
  const { mutateAsync: resetMappingRule, isPending } = useMutation({
    mutationFn: (idLegacyMappingRule: number) =>
      customFetcher({
        endpoint: '/arno/mapping-rules/reset',
        method: 'POST',
        body: { idLegacyMappingRule },
      }) as Promise<IArnoMappingRule>,
    onSuccess: async result => {
      await invalidateArno(queryClient);
      return result;
    },
  });

  return { resetMappingRule, isResettingMappingRule: isPending };
};
